import React, {useMemo, useState} from 'react';
import {Box, ToggleButton, ToggleButtonGroup} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import {GridViewRounded, ViewListRounded} from "@mui-symbols-material/w300";
import {UploadRounded} from "@mui-symbols-material/w200";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@apollo/client";
import _ from "lodash";
import {useTheme} from "@mui/material/styles";
import {useKeycloak} from "@react-keycloak/web";
import {toast} from "react-toastify";
import CircularProgress from "@mui/joy/CircularProgress";
import {DropzoneArea, Error, uploadFilesRequest} from "@dreebit-registry/vsm-microservice-components";
import {InstalledBaseNodeFiles} from "../../graphql/queries/installedBaseNodeFiles";
import {ProfileLanguage} from "../../graphql/queries/profileLanguage";
import {AddFilesToInstalledBaseNode} from "../../graphql/mutations/addFilesToInstalledBaseNode";
import {resolveErrorMessage, sanitizeErrorForDisplay} from "../../utils/resolveErrorMessage";
import {FileListViewMode, loadFileListViewMode, saveFileListViewMode} from "../../utils/fileListViewMode";
import {mapInstalledBaseNodeFiles, mapProductNodeFiles} from "../../utils/fileListItems";
import {buildNodeImageTarget, nodeTypeLabelForSentence} from "../../hooks/useNodeImage";
import {getInstalledBaseNodeTypeName} from "../../utils/getInstalledBaseNodeTypeName";
import FileList, {FileListItem} from "../FileList";

interface Props {
    nodeId: string;
    /** node.access.write — hides the upload dropzone for read-only users. */
    canWrite: boolean;
}

/**
 * Documents tab body of the node detail panel: the asset detail view's file
 * management without the filter rail — a compact full-width dropzone strip
 * above the file list. For asset nodes the read-only product documents are
 * merged in, exactly like in AssetFileManagement.
 */
const InstalledBaseNodeFileManagement = ({nodeId, canWrite}: Props) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const {keycloak} = useKeycloak();

    const profileLanguageQuery = useQuery(ProfileLanguage);
    const userLanguage = _.get(profileLanguageQuery, 'data.profile.language');

    const filesQuery = useQuery(InstalledBaseNodeFiles, {
        variables: {
            id: nodeId,
        }
    });

    const [addFilesToInstalledBaseNodeMutation] = useMutation(AddFilesToInstalledBaseNode);

    const [dropzoneKey, setDropzoneKey] = useState(0);

    const [viewMode, setViewMode] = useState<FileListViewMode>(() => loadFileListViewMode());

    const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newMode: FileListViewMode | null) => {
        if (!newMode) return; // ignore deselect → keep one option active
        setViewMode(newMode);
        saveFileListViewMode(newMode);
    };

    const installedBaseNodeFiles = _.get(filesQuery, 'data.installedBaseNode.fileList.installedBaseNodeFiles', []) ?? [];
    const productNodeFiles = _.get(filesQuery, 'data.installedBaseNode.assetLocation.asset.product.fileList.productNodeFiles', []) ?? [];
    const isLoading = filesQuery?.loading;

    const allFiles: FileListItem[] = useMemo(() => [
        ...mapInstalledBaseNodeFiles(installedBaseNodeFiles, t("User files")),
        ...mapProductNodeFiles(productNodeFiles),
    ], [installedBaseNodeFiles, productNodeFiles, t]);

    // "Use as <type> image" on image files — needs key/title for the update mutation.
    const fileNode = filesQuery.data?.installedBaseNode;
    const nodeImageTarget = buildNodeImageTarget(
        fileNode,
        nodeTypeLabelForSentence(fileNode ? getInstalledBaseNodeTypeName(fileNode.nodeType, t) : undefined, i18n.language)
    ) ?? undefined;

    const uploadFiles = async (files: File[]) => {
        if (files && files.length > 0) {
            try {
                // upload the files to the file microservice (POST /file)
                const uploadedFiles = await uploadFilesRequest(files, keycloak.token);

                const mutationInput = uploadedFiles.map((file) => {
                    return {
                        filename: file.filename,
                        fileServiceUuid: file.fileId,
                    }
                })

                // attach the uploaded files to the installed base node
                const res = await addFilesToInstalledBaseNodeMutation({
                    variables: {
                        installedBaseNodeId: nodeId,
                        input: mutationInput
                    },
                    refetchQueries: ["InstalledBaseNodeFiles"]
                })

                if (res?.data?.addFilesToInstalledBaseNode?.length > 0) {
                    toast.success(t("File(s) uploaded succesfully."));
                }
            } catch (err) {
                toast.error(resolveErrorMessage(err, t))
            } finally {
                setDropzoneKey((k) => k + 1);
            }
        }
    }

    if (isLoading) {
        return (
            <Box sx={{flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <CircularProgress variant="soft"/>
            </Box>
        );
    }

    if (filesQuery.error) {
        return (
            <Box sx={{flex: 1, minWidth: 0, p: '20px'}}>
                <Error errorObject={sanitizeErrorForDisplay(filesQuery.error, t)}/>
            </Box>
        );
    }

    return (
        <Box
            data-testid="node-detail-documents"
            sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                px: '20px',
                pt: '8px',
                pb: '12px',
                gap: '8px',
            }}
        >
            <Box sx={{display: 'flex', justifyContent: 'flex-end', minHeight: '32px', flexShrink: 0}}>
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    size="small"
                    onChange={handleViewChange}
                    aria-label={t("View")}
                    sx={{flexShrink: 0, height: '32px'}}
                >
                    <ToggleButton value="list" aria-label={t("List view")} sx={{px: '8px', py: 0}}>
                        <Tooltip title={t("List view")}>
                            <ViewListRounded sx={{fontSize: 20}}/>
                        </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="grid" aria-label={t("Tile view")} sx={{px: '8px', py: 0}}>
                        <Tooltip title={t("Tile view")}>
                            <GridViewRounded sx={{fontSize: 20}}/>
                        </Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {canWrite && (
                // Compact full-width strip instead of the asset view's square
                // rail dropzone (same override technique as FilesDropzone).
                <Box
                    sx={{
                        flexShrink: 0,
                        '& .asset-file-management-file-upload-dropzone': {
                            aspectRatio: 'auto !important',
                            height: '140px !important',
                        },
                    }}
                >
                    <DropzoneArea
                        key={dropzoneKey}
                        theme={theme}
                        fileObjects={[]}
                        onChange={(loadedFiles: File[]) => {
                            uploadFiles(loadedFiles)
                        }}
                        onDelete={() => {}}
                        filesLimit={10}
                        maxFileSize={999999999}
                        showPreviewsInDropzone={false}
                        showFileNames={true}
                        dropzoneText={t("Click here or drag files to this area to upload.")}
                        dropzoneClass={"asset-file-management-file-upload-dropzone"}
                        dropzoneParagraphClass={"custom-file-upload-textbox"}
                        showAlerts={['error']}
                        Icon={UploadRounded}
                        classes={{
                            text: "asset-file-management-dropzone-text",
                            root: "dropzone-root",
                            icon: "asset-file-management-dropzone-icon"
                        }}
                    />
                </Box>
            )}

            <Box sx={{flex: 1, minHeight: 0}}>
                <FileList files={allFiles} userLanguage={userLanguage} viewMode={viewMode} height="100%" nodeImageTarget={nodeImageTarget}/>
            </Box>
        </Box>
    );
};

export default InstalledBaseNodeFileManagement;
